import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react"
import TwoLine from "./svgs/TwoLine"
import Close from "./svgs/Close"
import useSettings from "../hooks/useSettings"
import useTheme from "../hooks/useTheme"

const SettingPopup = () => {
  const { deadline, slippage, setDeadline, setSlippage } = useSettings()
  const { darkMode } = useTheme()

  const warning =
    slippage < 0.5
      ? "Your transaction may fail"
      : slippage >= 50
      ? "Enter a valid slippage percentage"
      : slippage >= 2
      ? "Your transaction may be frontrun"
      : undefined

  return (
    <Menu>
      <MenuButton
        className={`flex items-center justify-center w-12 h-12 rounded-full transition-all ${
          darkMode ? "bg-[#131823] text-white" : "text-[#222] bg-white"
        }`}
      >
        <TwoLine />
      </MenuButton>
      <Transition
        enter="transition ease-out duration-75"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transition ease-in duration-100"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-95"
      >
        <MenuItems
          anchor="bottom end"
          className={`relative mt-2 rounded-xl w-[375px] ${
            darkMode
              ? "bg-[#131823] shadow-none text-white"
              : "bg-white shadow-[0px_8px_24px_-8px_rgba(175,178,237,0.24)] text-black"
          }`}
        >
          <MenuItem>
            {({ close }) => (
              <>
                <h4
                  className={`px-3 py-2 ${
                    darkMode ? "text-white" : "text-[#222]"
                  }`}
                >
                  Settings
                </h4>
                <button
                  className={`flex items-center justify-center absolute top-2 right-2 w-6 h-6 hover:bg-black/5 transition-all rounded-md ${
                    darkMode ? "text-white" : "text-[#222]"
                  }`}
                  onClick={close}
                >
                  <Close className="w-2.5 h-2.5" />
                </button>
                <div className="flex flex-col py-4 px-3 space-y-4">
                  <div className="flex flex-col space-y-2">
                    <h4
                      className={`text-sm ${
                        darkMode ? "text-white" : "text-[#222]"
                      }`}
                    >
                      Slippage Tolerance
                    </h4>
                    <div className="flex items-center justify-between space-x-2">
                      <div className="flex items-center space-x-1">
                        <button
                          data-active={slippage === 0.1}
                          className={`rounded-xl font-semibold h-10 w-[70px] transition-all ${
                            darkMode
                              ? "data-[active=true]:bg-[#324054] hover:bg-[#324054] focus:bg-[#324054]"
                              : "data-[active=true]:bg-[#e8edfa] hover:bg-[#e8edfa] focus:bg-[#e8edfa]"
                          }`}
                          onClick={() => setSlippage(0.1)}
                        >
                          0.1%
                        </button>
                        <button
                          data-active={slippage === 0.5}
                          className={`rounded-xl font-semibold h-10 w-[70px] transition-all ${
                            darkMode
                              ? "data-[active=true]:bg-[#324054] hover:bg-[#324054] focus:bg-[#324054]"
                              : "data-[active=true]:bg-[#e8edfa] hover:bg-[#e8edfa] focus:bg-[#e8edfa]"
                          }`}
                          onClick={() => setSlippage(0.5)}
                        >
                          0.5%
                        </button>
                        <button
                          data-active={slippage === 1}
                          onClick={() => setSlippage(1)}
                          className={`rounded-xl font-semibold h-10 w-[70px] transition-all ${
                            darkMode
                              ? "data-[active=true]:bg-[#324054] hover:bg-[#324054] focus:bg-[#324054]"
                              : "data-[active=true]:bg-[#e8edfa] hover:bg-[#e8edfa] focus:bg-[#e8edfa]"
                          }`}
                        >
                          1%
                        </button>
                      </div>
                      <div className="relative w-full">
                        <input
                          type="number"
                          value={slippage}
                          onChange={(e) => setSlippage(e.target.valueAsNumber)}
                          onBlur={() =>
                            setSlippage(
                              slippage >= 50 || !slippage ? 0.5 : slippage
                            )
                          }
                          placeholder="0.5"
                          className="w-full h-10 px-4 border font-semibold border-[#e8edfa] hover:border-[#e8edfa] rounded-2xl transition-all bg-transparent focus:border-[#e8edfa] focus:shadow-[0px_0px_0px_1px_#e8edfa] outline-none placeholder:font-normal"
                        />
                        <span
                          className={`absolute top-0 right-0 w-10 h-10 flex justify-center items-center ${
                            darkMode ? "text-white" : "text-[#31291e]"
                          }`}
                        >
                          %
                        </span>
                      </div>
                    </div>
                    {warning ? (
                      <p
                        className={`${
                          slippage >= 50 ? "text-[#E53E3E]" : "text-[#dbb053]"
                        } text-sm`}
                      >
                        {warning}
                      </p>
                    ) : null}
                  </div>
                  <div className="flex flex-col space-y-2">
                    <h4
                      className={`text-sm ${
                        darkMode ? "text-white" : "text-[#222]"
                      }`}
                    >
                      Transaction Deadline
                    </h4>
                    <div className="relative w-[210px]">
                      <input
                        type="number"
                        placeholder="30"
                        value={deadline}
                        onChange={(e) => setDeadline(e.target.valueAsNumber)}
                        className="w-full h-10 px-4 border font-semibold border-[#e8edfa] hover:border-[#e8edfa] rounded-2xl transition-all bg-transparent focus:border-[#e8edfa] focus:shadow-[0px_0px_0px_1px_#e8edfa] outline-none placeholder:font-normal"
                      />
                      <span
                        className={`absolute top-0 right-4 h-10 flex justify-center items-center ${
                          darkMode ? "text-white" : "text-[#06070a]"
                        }`}
                      >
                        minutes
                      </span>
                    </div>
                  </div>
                </div>
              </>
            )}
          </MenuItem>
        </MenuItems>
      </Transition>
    </Menu>
  )
}

export default SettingPopup
